import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { Refresh, Previous } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo, Spinning, UnderConstruction, DomainDetails, DropButton, AppButton, EmbeddedVideoPlayer,
} from '@Components/Control';
import { AuthNavWrapper } from '@Components/Navigation';
import { withProductAuth } from '@Components/Layout';
import {
  initiateResultDetailRequest,
  clearResultDetailRequest,
  initiateLiveTrackerAnalyticsRequest,
  clearLiveTrackerAnalyticsRequest,
  updateProductReviewSelectionsRequest,
} from '@Actions';
import { buildDateTimeLabel, PLATFORM_DISPLAY, getStatusDisplay } from '@Helpers/util';
import { paths } from '@Components/configs';
import { CellDropMenu } from '@Components/Partial/Product/Dashboard';


const MetadataRow = ({
  label, value, textColor, secondaryText, isUrl = false,
}) => {
  if (value == null || value === '') return null;

  return (
    <Box direction="row" gap="0.5rem" align="start" pad={{ vertical: '0.35rem' }}>
      <Text size="0.85rem" weight={600} color={textColor} style={{ minWidth: '13rem' }}>
        {label}
      </Text>
      {isUrl ? (
        <Text
          as="a"
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          size="0.85rem"
          color="var(--page-url-color)"
          style={{ wordBreak: 'break-all' }}
        >
          {value}
        </Text>
      ) : (
        <Text size="0.85rem" color={secondaryText} style={{ wordBreak: 'break-all' }}>
          {value}
        </Text>
      )}
    </Box>
  );
};

MetadataRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  textColor: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
  isUrl: PropTypes.bool,
};

const SectionHeader = ({ title, textColor }) => (
  <Text
    size="1rem"
    weight={700}
    color={textColor}
    style={{ marginBottom: '0.5rem', display: 'block' }}
  >
    {title}
  </Text>
);

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
};

const ResultDetailsPage = ({
  small,
  location,
  loading,
  resultDetail = null,
  fetchResultDetail,
  clearResultDetail,
  hostingData = null,
  fetchHostingData,
  clearHostingData,
  updateProductReviewSelections,
  resultsDisabled = false,
}) => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const {
    theme: { page, button },
    constructionImage,
    isDarkMode,
    disableResultsFeatures,
  } = useAppTheme();

  const [localReviewStatus, setLocalReviewStatus] = React.useState(null);

  React.useEffect(() => {
    fetchResultDetail(resultId);
    fetchHostingData();
    return () => {
      clearResultDetail();
      clearHostingData();
    };
  }, [resultId]);

  React.useEffect(() => {
    if (resultDetail?.reviewData?.reviewStatus) {
      setLocalReviewStatus(resultDetail.reviewData.reviewStatus);
    }
  }, [resultDetail?.reviewData?.reviewStatus]);

  const padding = small ? '1.5rem' : '2rem';
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const underMaintenance = disableResultsFeatures || resultsDisabled;

  const videoUrl = resultDetail?.signedVideoUrl || null;

  const matchedHost = resultDetail && hostingData?.data
    ? hostingData.data.find(
      (item) => item.host === resultDetail.pageDomainBreakdown?.domain
        || item.host === resultDetail.pageDomainBreakdown?.domainWithSubdomain,
    ) || null
    : null;

  const secondaryText = page.secondaryText || page.tableText;

  const cardStyle = {
    border: `1px solid ${page.tableBorder || page.inputBorder}`,
  };

  const handleReviewSubmit = () => {
    const prevStatus = resultDetail?.reviewData?.reviewStatus;
    /* eslint-disable camelcase */
    updateProductReviewSelections(
      { data: [{ id: resultDetail.id, review_status: localReviewStatus, prev_status: prevStatus }] },
      () => fetchResultDetail(resultId),
    );
    /* eslint-enable camelcase */
  };

  const renderHostingContent = () => {
    if (hostingData === null) {
      return (
        <Box align="center" justify="center" pad="2rem">
          <Spinning size="medium" color={page.highlight} />
        </Box>
      );
    }

    if (!matchedHost) {
      return (
        <Text size="0.85rem" color={secondaryText}>
          No hosting data is currently available for this domain.
        </Text>
      );
    }

    const globalRanking = matchedHost.globalRanking != null ? String(matchedHost.globalRanking) : null;

    return (
      <>
        <MetadataRow
          label="Host"
          value={matchedHost.host}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="IP Address"
          value={matchedHost.ipAddress}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="IP Geolocation"
          value={matchedHost.ipGeolocation}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="IP Timestamp"
          value={matchedHost.ipTimestamp ? buildDateTimeLabel(matchedHost.ipTimestamp) : null}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="Hosting Provider"
          value={matchedHost.hostingProviderName || matchedHost.hostingProvider}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="ISP"
          value={matchedHost.internetServiceProviderName || matchedHost.ispName}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="ISP Domain"
          value={matchedHost.ispDomain || matchedHost.internetServiceProviderDomain}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="Underlying ISP"
          value={matchedHost.underlyingIsp || matchedHost.underlyingInternetServiceProviderName}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="ISP Geolocation"
          value={matchedHost.internetServiceProviderGeolocation}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="Monthly Visitors (est.)"
          value={matchedHost.visitors != null ? String(matchedHost.visitors) : null}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
        <MetadataRow
          label="Global Ranking"
          value={globalRanking}
          textColor={page.primaryText}
          secondaryText={secondaryText}
        />
      </>
    );
  };

  const renderContent = () => {
    if (underMaintenance) {
      return (
        <Box flex align="center" justify="center" background={usePageBg}>
          <UnderConstruction
            imageUrl={constructionImage}
            header="Under Maintenance"
            message="System currently undergoing maintenance. Please check back later."
          />
        </Box>
      );
    }

    if (loading || !resultDetail) {
      return (
        <Box flex align="center" justify="center" background={usePageBg}>
          <Spinning size="large" color={page.highlight} />
        </Box>
      );
    }

    const {
      pageUrl,
      domain,
      language,
      recordedAt,
      matchTimestamp,
      searchId,
      platform,
      linkUrl,
      snapshotUrl,
      broadcasterName,
      referenceUrl,
      embedUrl,
    } = resultDetail;

    const reviewData = resultDetail?.reviewData;
    const isReviewable = reviewData?.isReviewable;
    const statusOptions = reviewData?.statusOptions;
    const reviewStatusDisplay = getStatusDisplay(
      isReviewable ? localReviewStatus : resultDetail.reviewStatus,
      statusOptions,
    );
    const submitDisabled = localReviewStatus === reviewData?.reviewStatus;

    const extraUrls = [
      { label: 'Link URL', url: linkUrl },
    ].filter(({ url }) => url);

    const hasEvidenceContent = videoUrl || snapshotUrl || recordedAt || matchTimestamp
      || broadcasterName || referenceUrl || embedUrl;

    return (
      <Box flex background={usePageBg} pad={padding} direction="column" gap="2rem">
        <Box direction={small ? 'column' : 'row'} gap="2rem" align="start">
          <Box
            flex
            pad={padding}
            round="var(--app-radius)"
            background={page.cardBg || page.background}
            style={cardStyle}
          >
            <SectionHeader title="Result Information" textColor={page.primaryText} />
            {pageUrl && (
              <Box direction="row" gap="0.5rem" align="start" pad={{ vertical: '0.35rem' }}>
                <Text size="0.85rem" weight={600} color={page.primaryText} style={{ minWidth: '13rem' }}>
                  URL
                </Text>
                <DomainDetails
                  small={small}
                  modalTitle="URL details"
                  primaryUrl={pageUrl}
                  extraUrls={extraUrls}
                  dmcaListsEnabled={false}
                  handleDomainUpdate={() => null}
                />
              </Box>
            )}
            {searchId && (
              <Box direction="row" gap="0.5rem" align="start" pad={{ vertical: '0.35rem' }}>
                <Text size="0.85rem" weight={600} color={page.primaryText} style={{ minWidth: '13rem' }}>
                  Search
                </Text>
                <Text
                  size="0.85rem"
                  color="var(--page-url-color)"
                  style={{ cursor: 'pointer', wordBreak: 'break-all' }}
                  onClick={() => navigate(paths.productSearchResults.replace(':id', searchId))}
                >
                  View search for this result
                </Text>
              </Box>
            )}
            <MetadataRow label="Domain" value={domain} textColor={page.primaryText} secondaryText={secondaryText} />
            <MetadataRow
              label="Platform"
              value={platform ? (PLATFORM_DISPLAY[platform] || platform) : null}
              textColor={page.primaryText}
              secondaryText={secondaryText}
            />
            <MetadataRow label="Language" value={language} textColor={page.primaryText} secondaryText={secondaryText} />
            <Box direction="row" gap="0.5rem" align="start" pad={{ vertical: '0.35rem' }}>
              <Text size="0.85rem" weight={600} color={page.primaryText} style={{ minWidth: '13rem' }}>
                Review Status
              </Text>
              {isReviewable ? (
                <Box direction="row" gap="0.5rem" align="center">
                  <DropButton
                    withCloseDropContent={(onClose) => (
                      <CellDropMenu
                        options={statusOptions}
                        onClick={(option) => {
                          setLocalReviewStatus(option.api);
                          onClose();
                        }}
                      />
                    )}
                  >
                    {(Icon) => (
                      <AppButton
                        dropDown
                        tableButtonStyle
                        color={button.tableText}
                        fontWeight={600}
                        label={reviewStatusDisplay}
                        icon={<Icon color={button.tableIcon} size="0.9rem" />}
                        focusHighlight={page.focusHighlight}
                      />
                    )}
                  </DropButton>
                  <AppButton
                    overrideHover
                    disabled={submitDisabled}
                    onClick={handleReviewSubmit}
                    level="tableControls"
                    color={button.primaryText}
                    bgColor={button.primaryBg}
                    label="Submit"
                  />
                </Box>
              ) : (
                reviewStatusDisplay && (
                  <Text size="0.85rem" color={secondaryText}>
                    {reviewStatusDisplay}
                  </Text>
                )
              )}
            </Box>
          </Box>
          <Box
            flex
            pad={padding}
            round="var(--app-radius)"
            background={page.cardBg || page.background}
            style={cardStyle}
          >
            <SectionHeader title="Hosting Data" textColor={page.primaryText} />
            {renderHostingContent()}
          </Box>
        </Box>
        {hasEvidenceContent && (
          <Box
            round="var(--app-radius)"
            overflow="hidden"
            background={page.cardBg || page.background}
            style={cardStyle}
          >
            <Box pad={{ horizontal: padding, top: padding, bottom: '0.75rem' }}>
              <Text size="1rem" weight={700} color={page.primaryText}>
                Evidence Recording
              </Text>
            </Box>
            {(recordedAt || matchTimestamp || broadcasterName || referenceUrl || embedUrl) && (
              <Box pad={{ horizontal: padding, bottom: (snapshotUrl || videoUrl) ? '1rem' : padding }}>
                <MetadataRow
                  label="Recorded At"
                  value={recordedAt ? buildDateTimeLabel(recordedAt) : null}
                  textColor={page.primaryText}
                  secondaryText={secondaryText}
                />
                <MetadataRow
                  label="Match Timestamp"
                  value={matchTimestamp}
                  textColor={page.primaryText}
                  secondaryText={secondaryText}
                />
                <MetadataRow
                  label="Broadcaster Name"
                  value={broadcasterName}
                  textColor={page.primaryText}
                  secondaryText={secondaryText}
                />
                <MetadataRow
                  label="Reference URL"
                  value={referenceUrl}
                  textColor={page.primaryText}
                  secondaryText={secondaryText}
                  isUrl
                />
                <MetadataRow
                  label="Embed URL"
                  value={embedUrl}
                  textColor={page.primaryText}
                  secondaryText={secondaryText}
                  isUrl
                />
              </Box>
            )}
            {(() => {
              let mediaContent = null;
              if (snapshotUrl) {
                mediaContent = { type: 'snapshot', url: snapshotUrl };
              } else if (videoUrl) {
                mediaContent = { type: 'video', url: videoUrl };
              }

              if (!mediaContent) return null;
              if (mediaContent.type === 'snapshot') {
                return (
                  <Box pad={{ horizontal: padding, bottom: padding }}>
                    <img
                      src={mediaContent.url}
                      alt="Page snapshot"
                      style={{ maxWidth: '100%', borderRadius: 'var(--app-radius)' }}
                    />
                  </Box>
                );
              }
              return (
                <Box pad={{ horizontal: padding, bottom: padding }}>
                  <EmbeddedVideoPlayer
                    url={mediaContent.url}
                    fileName={resultDetail.fileName}
                  />
                </Box>
              );
            })()}
          </Box>
        )}
      </Box>
    );
  };

  let pageTitle = 'Loading details...';
  if (resultDetail) {
    pageTitle = `${resultDetail.title || resultDetail.pageUrl || resultDetail.domain || resultDetail.id}`;
  }

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: pageTitle,
        subTitle: 'Results',
        bannerChildrenPosition: 'bottom',
        bannerChildren: !loading && (
          <Box width="fit-content" direction="row" gap="1rem">
            <AppButton
              overrideHover
              onClick={() => navigate(-1)}
              label="Go back"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Previous size="0.8rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
            <AppButton
              overrideHover
              onClick={() => fetchResultDetail(resultId)}
              label="Refresh"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Refresh size="0.9rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
              disabled={!resultId}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      {renderContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    resultDetail: state.productResults.resultDetail,
    loading: state.fetchLoader.dataLoading,
    hostingData: state.analytics.liveTrackerData,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchResultDetail: initiateResultDetailRequest,
    clearResultDetail: clearResultDetailRequest,
    fetchHostingData: initiateLiveTrackerAnalyticsRequest,
    clearHostingData: clearLiveTrackerAnalyticsRequest,
    updateProductReviewSelections: updateProductReviewSelectionsRequest,
  }, dispatch);
}

ResultDetailsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  resultDetail: PropTypes.shape({
    id: PropTypes.string,
    pageUrl: PropTypes.string,
    domain: PropTypes.string,
    language: PropTypes.string,
    recordedAt: PropTypes.string,
    matchTimestamp: PropTypes.string,
    filePath: PropTypes.string,
    fileName: PropTypes.string,
    reviewStatus: PropTypes.string,
    signedVideoUrl: PropTypes.string,
    linkUrl: PropTypes.string,
    snapshotUrl: PropTypes.string,
    broadcasterName: PropTypes.string,
    referenceUrl: PropTypes.string,
    embedUrl: PropTypes.string,
    title: PropTypes.string,
    searchId: PropTypes.number,
    platform: PropTypes.string,
    pageDomainBreakdown: PropTypes.shape({
      domain: PropTypes.string,
      domainWithSubdomain: PropTypes.string,
    }),
    reviewData: PropTypes.shape({
      isReviewable: PropTypes.bool,
      reviewStatus: PropTypes.string,
      statusOptions: PropTypes.arrayOf(PropTypes.shape({
        api: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
      })),
    }),
  }),
  fetchResultDetail: PropTypes.func.isRequired,
  clearResultDetail: PropTypes.func.isRequired,
  hostingData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      host: PropTypes.string,
    })),
  }),
  fetchHostingData: PropTypes.func.isRequired,
  clearHostingData: PropTypes.func.isRequired,
  updateProductReviewSelections: PropTypes.func.isRequired,
  resultsDisabled: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ResultDetailsPage));
